import { space } from '@op/opds.tokens';
import { Message, useMessage, useMessageGetter } from '@messageformat/react';
import { ReactElement, useRef, useState } from 'react';
import { Col, Expander, Row, Tab, Tabs } from '@op/opds.opux';
import {
  IconInternational,
  IconAdmin,
  IconProperty,
  IconNetwork,
  IconBank,
} from '@op/opds.opux-icons/misc';
import { IconCorporateSmall } from '@op/opds.opux-icons/service';
import { IconFunds, IconSustainability } from '@op/opds.opux-icons/finance';
import { IconBuy } from '@op/opds.opux-icons/action';
import { ReactComponent as KeysImage } from '../../images/OP_symbolikuvitus_128_011.svg';
import { ReactComponent as FolderImage } from '../../images/OP_symbolikuvitus_128_026.svg';
import { ReactComponent as CoinsImage } from '../../images/OP_symbolikuvitus_128_008.svg';
import { ReactComponent as PaperChartImage } from '../../images/OP_symbolikuvitus_128_054.svg';
import { ReactComponent as TimeImage } from '../../images/OP_symbolikuvitus_128_037.svg';
import { ReactComponent as ExpensesImage } from '../../images/OP_symbolikuvitus_128_046.svg';
import { ReactComponent as MobileLockImage } from '../../images/OP_symbolikuvitus_128_028.svg';
import { ReactComponent as LetterImage } from '../../images/OP_symbolikuvitus_128_030.svg';
import { ReactComponent as SupportImage } from '../../images/OP_symbolikuvitus_128_044.svg';
import TaskOne from './TaskOne/TaskOne';
import CourseHeader from '../CourseHeader';
import TaskTwo from './TaskTwo/TaskTwo';
import CompareTable from './CompareTable';
import HighlightedList from '../HighlightedList';
import InfoTextBox from '../InfoTextBox';
import TextComponent from '../TextComponent';
import parseMsgContent from '../../util/parseMsgContent';
import CourseContent from '../CourseContent';
import TimeSpan from './TimeSpan/Timespan';
import InsuranceSaving from './InsuranceSaving';
import CourseComplete from '../CourseComplete';
import ReturnToTop from '../ReturnToTop';

const infoTextBox1Items = [
  { icon: IconInternational, messageId: 'course6.tab2.infobox1.subtitle1' },
  { icon: IconCorporateSmall, messageId: 'course6.tab2.infobox1.subtitle2' },
  { icon: IconAdmin, messageId: 'course6.tab2.infobox1.subtitle3' },
  { icon: IconFunds, messageId: 'course6.tab2.infobox1.subtitle4' },
  { icon: IconProperty, messageId: 'course6.tab2.infobox1.subtitle5' },
  { icon: IconNetwork, messageId: 'course6.tab2.infobox1.subtitle6' },
  { icon: IconSustainability, messageId: 'course6.tab2.infobox1.subtitle7' },
];

const infoTextBox2Items = [
  { icon: IconInternational, messageId: 'course6.tab2.infobox2.subtitle1' },
  { icon: IconCorporateSmall, messageId: 'course6.tab2.infobox2.subtitle2' },
  { icon: IconAdmin, messageId: 'course6.tab2.infobox2.subtitle3' },
  { icon: IconFunds, messageId: 'course6.tab2.infobox2.subtitle4' },
];

const infoTextBox3Items = [
  { icon: IconInternational, messageId: 'course6.tab2.infobox3.subtitle1' },
  { icon: IconCorporateSmall, messageId: 'course6.tab2.infobox3.subtitle2' },
  { icon: IconAdmin, messageId: 'course6.tab2.infobox3.subtitle3' },
];

const infoTextBox4Items = [
  { icon: IconInternational, messageId: 'course6.tab2.infobox4.subtitle1' },
  { icon: IconAdmin, messageId: 'course6.tab2.infobox4.subtitle2' },
];

const infoTextBox5Items = [
  { icon: IconBuy, messageId: 'course6.infobox5.subtitle1' },
  { icon: IconAdmin, messageId: 'course6.infobox5.subtitle2' },
  { icon: IconBank, messageId: 'course6.infobox5.subtitle3' },
];

const Course6 = (): ReactElement => {
  const getCourseTexts = useMessageGetter('course6');
  const investemntLaw = useMessage('course6.content1');
  const firstFundsTab = useMessage('course6.tab1.content1');
  const firstStocksTab = useMessage('course6.tab1.content2');
  const thirdFundsTab = useMessage('course6.tab3.content1');
  const thirdStocksTab = useMessage('course6.tab3.content2');
  const fourthFundsTab = useMessage('course6.tab4.content1');
  const fourthStocksTab = useMessage('course6.tab4.content2');
  const investmentTime = useMessage('course6.content4');

  const parsedLaw = parseMsgContent(investemntLaw);
  const parsedFirstFunds = parseMsgContent(firstFundsTab);
  const parsedFirstStocks = parseMsgContent(firstStocksTab);
  const parsedThirdFunds = parseMsgContent(thirdFundsTab);
  const parsedThirdStocks = parseMsgContent(thirdStocksTab);
  const parsedFourthFunds = parseMsgContent(fourthFundsTab);
  const parsedFourthStocks = parseMsgContent(fourthStocksTab);
  const parsedTime = parseMsgContent(investmentTime);

  const targetRef = useRef<HTMLDivElement>(null);
  const targetRef2 = useRef<HTMLDivElement>(null);
  const targetRef3 = useRef<HTMLDivElement>(null);
  const targetRef4 = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<{ [key: string]: number }>({
    first_div: 0,
    second_div: 2,
    third_div: 4,
    fourth_div: 7,
  });

  const handleTabChange = (divId: string, index: number): void => {
    setActiveTab((prev) => ({
      ...prev,
      [divId]: index,
    }));

    if (divId === 'second_div') {
      setActiveTab((prev) => ({
        ...prev,
        second_div: index + 2,
      }));
    }
    if (divId === 'third_div') {
      setActiveTab((prev) => ({
        ...prev,
        third_div: index + 4,
      }));
    }
    if (divId === 'fourth_div') {
      setActiveTab((prev) => ({
        ...prev,
        fourth_div: index + 7,
      }));
    }
  };

  return (
    <div style={{ marginBottom: space.default }}>
      <CourseHeader
        courseTitleId="course6.title"
        courseHeaderText="learn_more"
        Image={KeysImage}
        courseNumber={6}
      />
      <div style={{ textAlign: 'center', paddingBottom: space.default }}>
        <TextComponent messageId="course6.introduction" twoColumns={false} />
      </div>
      <div style={{ paddingTop: space.small, paddingBottom: space.small }}>
        <FolderImage className="investoracademy-small-svg" aria-hidden="true" />
      </div>
      <CourseContent content={parsedLaw} headingAs="h2" />
      <div style={{ paddingTop: space.small, paddingBottom: space.default }}>
        <CoinsImage className="investoracademy-small-svg" aria-hidden="true" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle2" />
      </h2>
      <TextComponent messageId="course6.content2" twoColumns />
      <div style={{ paddingBottom: space.xxl }} ref={targetRef} id="first_div">
        <Tabs
          small
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 0 0',
            paddingTop: space.default,
            marginBottom: space.small,
          }}
          active={activeTab.first_div}
          onChange={(event, index) => handleTabChange('first_div', index)}
        >
          <Tab
            id="first_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle1')}
          >
            <CourseContent content={parsedFirstFunds} />
          </Tab>
          <Tab
            id="second_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle2')}
          >
            <CourseContent content={parsedFirstStocks} />
          </Tab>
        </Tabs>
        <ReturnToTop
          msgId="return_to_tabs"
          targetRef={targetRef}
          index={activeTab.first_div}
        />
      </div>

      <div style={{ paddingTop: space.xxl, paddingBottom: space.default }}>
        <PaperChartImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle3" />
      </h2>
      <p
        className="opds-styles-v1-text-body-medium"
        style={{ textAlign: 'center', paddingTop: space.default }}
      >
        <Message id="course6.content3" />
      </p>
      <div style={{ paddingBottom: space.xxl }} ref={targetRef2} id="second_div">
        <Tabs
          small
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 0 0',
            paddingTop: space.default,
            marginBottom: space.small,
          }}
          active={activeTab.second_div}
          onChange={(event, index) => handleTabChange('second_div', index)}
        >
          <Tab
            id="third_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle1')}
          >
            <Row style={{ margin: 0 }}>
              <Col md={6} xs={12} className="investoracademy-multi-column-left">
                <p
                  className="opds-styles-v1-text-body-medium"
                  style={{ paddingBottom: space.default }}
                >
                  <Message id="course6.tab2.content1" />
                </p>
                <InfoTextBox
                  titleId="course6.tab2.infobox1.title"
                  items={infoTextBox1Items}
                />
              </Col>
              <Col md={6} xs={12} className="investoracademy-multi-column-right">
                <p
                  className="opds-styles-v1-text-body-medium"
                  style={{ paddingBottom: space.default }}
                >
                  <Message id="course6.tab2.content2" />
                </p>
                <p className="opds-styles-v1-text-body-medium">
                  <Message id="course6.tab2.content3" />
                </p>
                <p className="opds-styles-v1-text-body-medium">
                  <Message id="course6.tab2.content4" />
                </p>
              </Col>
            </Row>
          </Tab>
          <Tab
            id="fourth_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle3')}
          >
            <Row style={{ margin: 0 }}>
              <Col md={6} xs={12} className="investoracademy-multi-column-left">
                <p
                  className="opds-styles-v1-text-body-medium"
                  style={{ paddingBottom: space.default }}
                >
                  <Message id="course6.tab2.content5" />
                </p>
                <InfoTextBox
                  titleId="course6.tab2.infobox2.title"
                  items={infoTextBox2Items}
                />
              </Col>
              <Col md={6} xs={12} className="investoracademy-multi-column-right">
                <InfoTextBox
                  titleId="course6.tab2.infobox3.title"
                  items={infoTextBox3Items}
                />
                <InfoTextBox
                  titleId="course6.tab2.infobox4.title"
                  items={infoTextBox4Items}
                />
              </Col>
            </Row>
          </Tab>
        </Tabs>
        <ReturnToTop
          msgId="return_to_tabs"
          targetRef={targetRef2}
          index={activeTab.second_div}
        />
      </div>

      <div style={{ paddingTop: space.xxl, paddingBottom: space.xs }}>
        <TimeImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <CourseContent content={parsedTime} headingAs="h2" />
      <div style={{ paddingTop: space.large, paddingBottom: space.default }}>
        <ExpensesImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle5" />
      </h2>

      <Row style={{ paddingBottom: space.xl, margin: 0 }}>
        <Col md={6} xs={12} className="investoracademy-multi-column-left">
          <p
            className="opds-styles-v1-text-body-medium"
            style={{ paddingBottom: space.default }}
          >
            <Message id="course6.content5" />
          </p>
          <InfoTextBox items={infoTextBox5Items} />
        </Col>
        <Col md={6} xs={12} className="investoracademy-multi-column-right">
          <p
            className="opds-styles-v1-text-body-medium"
            style={{ paddingBottom: space.default }}
          >
            <Message id="course6.content6" />
          </p>
        </Col>
      </Row>

      <div ref={targetRef3} id="third_div">
        <Tabs
          small
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 0 0',
            paddingTop: space.default,
            marginBottom: space.small,
          }}
          active={activeTab.third_div}
          onChange={(event, index) => handleTabChange('third_div', index)}
        >
          <Tab
            id="fifth_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle4')}
          >
            <CourseContent content={parsedThirdFunds} />
          </Tab>
          <Tab
            id="sixth_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle5')}
          >
            <CourseContent content={parsedThirdStocks} />
          </Tab>
        </Tabs>
        <ReturnToTop
          msgId="return_to_tabs"
          targetRef={targetRef3}
          index={activeTab.third_div}
        />
      </div>

      <TaskOne />

      <div style={{ marginTop: space.xxxl, paddingBottom: space.default }}>
        <MobileLockImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle6" />
      </h2>
      <TextComponent messageId="course6.content7" twoColumns />
      <h3
        className="opds-styles-v1-text-title-xxl"
        style={{
          textAlign: 'center',
          paddingBottom: space.default,
        }}
      >
        <Message id="course6.subtitle7" />
      </h3>
      <CompareTable />
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', paddingBottom: space.default, paddingTop: '0.5rem' }}
      >
        <Message id="course6.table1.info" />
      </p>
      <div style={{ marginTop: space.xxxl, paddingBottom: space.default }}>
        <LetterImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle8" />
      </h2>

      <div style={{ textAlign: 'center', paddingBottom: space.default }}>
        <TextComponent messageId="course6.content8" twoColumns={false} />
      </div>

      <Expander title={useMessage('course6.expanderTitle')}>
        <InsuranceSaving />
      </Expander>

      <div style={{ marginTop: space.xxxl, paddingBottom: space.default }}>
        <MobileLockImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle9" />
      </h2>

      <div style={{ textAlign: 'center', paddingBottom: space.default }}>
        <TextComponent messageId="course6.content9" twoColumns={false} />
      </div>

      <div style={{ paddingBottom: space.xxl }} ref={targetRef4} id="fourth_div">
        <Tabs
          small
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 0 0',
            paddingTop: space.default,
            marginBottom: space.small,
          }}
          active={activeTab.fourth_div}
          onChange={(event, index) => handleTabChange('fourth_div', index)}
        >
          <Tab
            id="eight_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle4')}
          >
            <h3 className="sr-only">{getCourseTexts('instruments.tabtitle4')}</h3>
            <CourseContent content={parsedFourthFunds} />
            <TimeSpan timeSpanKey="1" />
          </Tab>
          <Tab
            id="ninth_tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
            label={getCourseTexts('instruments.tabtitle5')}
          >
            <h3 className="sr-only">{getCourseTexts('instruments.tabtitle5')}</h3>
            <CourseContent content={parsedFourthStocks} />
            <TimeSpan timeSpanKey="2" />
          </Tab>
        </Tabs>
        <ReturnToTop
          msgId="return_to_tabs"
          targetRef={targetRef4}
          index={activeTab.fourth_div}
        />
      </div>

      <div style={{ paddingTop: space.xxs, paddingBottom: space.default }}>
        <SupportImage aria-hidden="true" className="investoracademy-small-svg" />
      </div>
      <h2 className="investoracademy-title-hero">
        <Message id="course6.subtitle10" />
      </h2>

      <TextComponent messageId="course6.content10" twoColumns />

      <TaskTwo />
      <HighlightedList
        title="course6.highlighted_message.title"
        messageId="course6.highlighted_message.body"
      />
      <CourseComplete messageId="course6.recap" />
    </div>
  );
};

export default Course6;
